/* eslint-disable react/jsx-no-useless-fragment */
import React, { useMemo } from 'react';

import { array, object, string } from 'yup';

import { DropdownMultiple, DropdownSingle } from 'src/app/components/lib/dropdown';
import { FormSection } from 'src/app/components/lib/form/form';
import { SummarySidebarSection } from 'src/app/pages/marketing/workflows/workflow-campaign-builder/summary-sidebar/styles';
import { useGetSegmentsQuery } from 'src/app/queries/segment/get-segments';

import { useWorkflowBuilderContext } from '../../wizard/builder-context';
import { validateWithSchema } from '../../wizard/helpers';

import type { ConditionalNodeData } from '../../slides/workflow-editor/models';

const conditionalTypeOptions = [
  {
    label: 'Clicked email',
    id: 'clickedEmail',
  },
  {
    label: 'Opened email',
    id: 'openedEmail',
  },
  {
    label: 'Opened push',
    id: 'openedPush',
  },
  {
    label: 'Viewed SMS message',
    id: 'openedSMS',
  },
  {
    label: 'Is in segment',
    id: 'isInSegment',
  },
  {
    label: 'Made a purchase',
    id: 'madeAPurchase',
  },
];

export const conditionalSchema = object({
  conditionalType: string().required().label('Conditional type'),
  config: object().when('conditionalType', {
    is: 'isInSegment',
    then: object({
      segmentIds: array().required().min(1).label('Segments'),
    }),
    otherwise: object({}),
  }),
});

export function ConditionalSection({
  nodeData,
  selectedNodeId,
}: {
  nodeData: ConditionalNodeData;
  selectedNodeId: string;
}) {
  const [builderState, dispatch] = useWorkflowBuilderContext();
  const { selectedNodeIsNew } = builderState;

  const { data: segments = [], isLoading: segmentsIsLoading } = useGetSegmentsQuery();
  const segmentOptions = useMemo(
    () => segments.map((segment) => ({ label: segment.Name, id: segment.SegmentId })),
    [segments]
  );

  const validationErrors = useMemo(
    () => validateWithSchema(conditionalSchema, nodeData, selectedNodeIsNew),
    [nodeData, selectedNodeIsNew]
  );

  const setConditionalType = (conditionalType: string) => {
    dispatch({
      type: 'set-node-data',
      payload: {
        nodeId: selectedNodeId,
        value: {
          conditionalType,
          config: {
            conditionalReferenceNodeId: null,
            duration: null,
            segmentIds: conditionalType === 'isInSegment' ? [] : null,
          },
        },
      },
    });
  };

  const setSelectedSegments = (segmentIds: number[]) => {
    dispatch({
      type: 'set-node-data',
      payload: {
        nodeId: selectedNodeId,
        value: {
          config: {
            conditionalReferenceNodeId: null,
            duration: null,
            segmentIds,
          },
        },
      },
    });
  };

  return (
    <SummarySidebarSection>
      <FormSection>
        <DropdownSingle
          automationId='campaign-workflow-builder-event-type-select'
          disabled={builderState.isReadOnly}
          helpText='Select an event'
          label='Event:'
          labelPlacement='top'
          options={conditionalTypeOptions.map((option) => ({
            label: option.label,
            id: option.id,
          }))}
          required
          setValue={(option) => setConditionalType(String(option.id))}
          value={nodeData.conditionalType || ''}
        />
        {nodeData.conditionalType === 'isInSegment' && nodeData.config.segmentIds && (
          <DropdownMultiple
            automationId='campaign-workflow-builder-segment-select'
            disabled={builderState.isReadOnly}
            errorMessage={validationErrors.getError('config.segmentIds')?.message}
            helpText='Select a segment'
            label='Segments:'
            labelPlacement='top'
            loading={segmentsIsLoading}
            options={segmentOptions}
            required
            setValue={(options) => setSelectedSegments(options.map((option) => Number(option.id)))}
            value={nodeData.config.segmentIds.map((segmentId) => ({
              label: segmentOptions.find((option) => option.id === segmentId)?.label ?? '',
              id: segmentId,
            }))}
          />
        )}
      </FormSection>
    </SummarySidebarSection>
  );
}
